import { isHighVolumeWsMessageType } from '@shared/protocol';

export type WsWireDirection = 'out' | 'in' | 'error';

export interface WsWireLogEntry {
  readonly id: number;
  readonly at: string;
  readonly direction: WsWireDirection;
  readonly stage: string;
  readonly type: string | null;
  readonly detail?: string;
  readonly payload?: unknown;
  readonly raw?: string;
}

export interface WsWireLogInput {
  readonly direction: WsWireDirection;
  readonly stage: string;
  readonly payload?: unknown;
  readonly raw?: unknown;
  readonly detail?: string;
}

export type WsWireLogSubscriber = (
  entry: WsWireLogEntry,
  entries: readonly WsWireLogEntry[],
) => void;

export const WS_WIRE_LOG_STORAGE_KEY = 'smashparty_ws_log';
const MAX_ENTRIES = 200;
const MAX_RAW_CHARS = 4_000;
const MAX_DEPTH = 8;
const REALTIME_SUMMARY_INTERVAL_MS = 5_000;

let nextId = 1;
let memoryLog: WsWireLogEntry[] = [];
const subscribers = new Set<WsWireLogSubscriber>();
const realtimeSummaries = new Map<
  string,
  {
    direction: WsWireDirection;
    stage: string;
    type: string | null;
    count: number;
    firstAtMs: number;
    lastAtMs: number;
  }
>();
let realtimeSummaryTimer: ReturnType<typeof setTimeout> | null = null;

export function getWsWireLog(): readonly WsWireLogEntry[] {
  const stored = readStoredLog();
  if (stored !== null) {
    memoryLog = stored;
  }
  return memoryLog;
}

export function subscribeWsWireLog(subscriber: WsWireLogSubscriber): () => void {
  subscribers.add(subscriber);
  return () => {
    subscribers.delete(subscriber);
  };
}

export function logClientWsEvent(input: WsWireLogInput): WsWireLogEntry {
  const payload = input.payload !== undefined ? sanitizeForClientLog(input.payload) : undefined;
  const raw = input.raw !== undefined ? truncateRaw(String(input.raw)) : undefined;
  const entry: WsWireLogEntry = {
    id: nextId++,
    at: new Date().toISOString(),
    direction: input.direction,
    stage: input.stage,
    type: extractType(payload),
    ...(input.detail !== undefined ? { detail: input.detail } : {}),
    ...(payload !== undefined ? { payload } : {}),
    ...(raw !== undefined ? { raw } : {}),
  };

  if (isHighVolumeWsMessageType(entry.type) && !isFullRealtimeWsLoggingEnabled()) {
    recordRealtimeSummary(entry);
    return entry;
  }

  appendStoredEntry(entry);
  return entry;
}

export function flushClientRealtimeWsLogSummaries(): void {
  if (realtimeSummaryTimer != null) {
    clearTimeout(realtimeSummaryTimer);
    realtimeSummaryTimer = null;
  }
  const summaries = [...realtimeSummaries.values()];
  realtimeSummaries.clear();
  for (const summary of summaries) {
    appendStoredEntry({
      id: nextId++,
      at: new Date().toISOString(),
      direction: summary.direction,
      stage: 'realtime_summary',
      type: summary.type,
      detail: `stage=${summary.stage} count=${summary.count}`,
      payload: {
        type: summary.type,
        originalStage: summary.stage,
        count: summary.count,
        firstAt: new Date(summary.firstAtMs).toISOString(),
        lastAt: new Date(summary.lastAtMs).toISOString(),
        intervalMs: summary.lastAtMs - summary.firstAtMs,
      },
    });
  }
}

export function resetWsWireLogForTests(): void {
  if (realtimeSummaryTimer != null) {
    clearTimeout(realtimeSummaryTimer);
    realtimeSummaryTimer = null;
  }
  realtimeSummaries.clear();
  memoryLog = [];
  nextId = 1;
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(WS_WIRE_LOG_STORAGE_KEY);
  } catch {
    // Test-only cleanup remains best-effort for storage shims.
  }
}

function appendStoredEntry(entry: WsWireLogEntry): void {
  const next = [...getWsWireLog(), entry].slice(-MAX_ENTRIES);
  memoryLog = next;
  writeStoredLog(next);
  debugToConsole(entry);
  for (const subscriber of subscribers) {
    subscriber(entry, next);
  }
}

function recordRealtimeSummary(entry: WsWireLogEntry): void {
  const key = `${entry.direction}|${entry.stage}|${entry.type ?? ''}`;
  const now = Date.now();
  const existing = realtimeSummaries.get(key);
  if (existing == null) {
    realtimeSummaries.set(key, {
      direction: entry.direction,
      stage: entry.stage,
      type: entry.type,
      count: 1,
      firstAtMs: now,
      lastAtMs: now,
    });
  } else {
    existing.count += 1;
    existing.lastAtMs = now;
  }
  if (realtimeSummaryTimer == null) {
    realtimeSummaryTimer = setTimeout(
      flushClientRealtimeWsLogSummaries,
      REALTIME_SUMMARY_INTERVAL_MS,
    );
  }
}

function isFullRealtimeWsLoggingEnabled(): boolean {
  const globalFlag = (globalThis as { __SMASHPARTY_WS_FULL_REALTIME_LOG__?: unknown })
    .__SMASHPARTY_WS_FULL_REALTIME_LOG__;
  if (globalFlag === true || globalFlag === 'true' || globalFlag === '1') return true;
  try {
    if (typeof localStorage === 'undefined') return false;
    const flag = localStorage.getItem('smashparty_ws_full_realtime_log');
    return flag === '1' || flag === 'true' || flag === 'yes' || flag === 'on';
  } catch {
    return false;
  }
}

function debugToConsole(entry: WsWireLogEntry): void {
  const tag = entry.direction === 'out' ? '[ws->]' : entry.direction === 'in' ? '[ws<-]' : '[ws!]';
  // eslint-disable-next-line no-console
  console.debug(tag, {
    at: entry.at,
    stage: entry.stage,
    type: entry.type,
    detail: entry.detail,
    payload: entry.payload,
    raw: entry.raw,
  });
}

function extractType(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const maybeType = (payload as { type?: unknown }).type;
  return typeof maybeType === 'string' ? maybeType : null;
}

function sanitizeForClientLog(value: unknown, depth = 0, seen = new WeakSet<object>()): unknown {
  if (depth > MAX_DEPTH) return '[MaxDepth]';
  if (typeof value === 'string') return value;
  if (typeof value !== 'object' || value === null) return value;
  if (seen.has(value)) return '[Circular]';
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeForClientLog(item, depth + 1, seen));
  }

  const out: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    if (isSensitiveClientLogKey(key)) {
      out[key] = redactSessionToken(item);
    } else {
      out[key] = sanitizeForClientLog(item, depth + 1, seen);
    }
  }
  return out;
}

function isSensitiveClientLogKey(key: string): boolean {
  return /^(sessiontoken|token|accesstoken|refreshtoken)$/i.test(key);
}

function redactSessionToken(value: unknown): unknown {
  if (typeof value !== 'string') return '[redacted]';
  const prefix = value.slice(0, 6);
  return `${prefix}...(${value.length})`;
}

function truncateRaw(raw: string): string {
  if (raw.length <= MAX_RAW_CHARS) return raw;
  return `${raw.slice(0, MAX_RAW_CHARS)}...[truncated ${raw.length - MAX_RAW_CHARS} chars]`;
}

function readStoredLog(): WsWireLogEntry[] | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(WS_WIRE_LOG_STORAGE_KEY);
    if (raw == null) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.slice(-MAX_ENTRIES) as WsWireLogEntry[];
  } catch {
    return null;
  }
}

function writeStoredLog(entries: readonly WsWireLogEntry[]): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(WS_WIRE_LOG_STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Best-effort diagnostic storage; console output still carries the event.
  }
}
